import { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Radio,
  Chip,
  Tooltip,
  Stack,
  FormControl,
  useTheme,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Search,
  ChevronDown,
  Plus,
  Info,
  ChevronsUpDown,
  RefreshCw,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getTemplatesApi } from "../../../api/chat.api";

const LANGUAGE_LABELS = {
  en: "English",
  en_US: "English (US)",
  en_GB: "English (UK)",
  hi: "Hindi",
  ta: "Tamil",
  es: "Spanish",
  es_ES: "Spanish (ES)",
  es_AR: "Spanish (AR)",
};

const STATUS_MAP = {
  APPROVED: "APPROVED",
  PENDING: "IN REVIEW",
  REJECTED: "REJECTED",
  PAUSED: "PAUSED",
  DISABLED: "DISABLED",
  IN_APPEAL: "IN APPEAL",
};

const getStatusChip = (status) => {
  const statusConfig = {
    "IN REVIEW": { bg: "#FEF9C3", color: "#A16207" },
    APPROVED: { bg: "#D1FAE5", color: "#047857" },
    REJECTED: { bg: "#FEE2E2", color: "#DC2626" },
    PAUSED: { bg: "#F3F4F6", color: "#6B7280" },
    DISABLED: { bg: "#F3F4F6", color: "#6B7280" },
    "IN APPEAL": { bg: "#FEF3C7", color: "#92400E" },
  };

  const config = statusConfig[status] || statusConfig["IN REVIEW"];

  return (
    <Chip
      label={status}
      size="small"
      sx={{
        bgcolor: config.bg,
        color: config.color,
        fontWeight: 500,
        fontSize: "10px",
        height: "22px",
        borderRadius: "4px",
        border: "none",
        "& .MuiChip-label": {
          px: 1,
        },
      }}
    />
  );
};

// Custom info icon component matching the reference
const InfoIcon = () => (
  <Box
    sx={{
      width: 14,
      height: 14,
      borderRadius: "50%",
      border: "1.5px solid #9CA3AF",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "9px",
      fontWeight: 600,
      color: "#9CA3AF",
      cursor: "pointer",
      ml: 0.5,
    }}
  >
    i
  </Box>
);

export default function TemplatesList() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState("");
  const [languageFilter, setLanguageFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateRange, setDateRange] = useState("");
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analyticsDisabled, setAnalyticsDisabled] = useState(false);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await getTemplatesApi();
      const resData = data?.data || {};
      const list = resData.templates || [];
      setAnalyticsDisabled(!!resData.analyticsDisabled);

      const mapped = list.map((t) => ({
        id: t.id,
        name: t.name,
        category: t.category || "—",
        language: LANGUAGE_LABELS[t.language] || t.language,
        languagePreview:
          (t.body?.text || "").length > 30
            ? t.body.text.slice(0, 30) + "..."
            : t.body?.text || "",
        status: STATUS_MAP[t.status] || t.status,
        sent: t.analytics?.sent ?? null,
        delivered: t.analytics?.delivered ?? null,
        read: t.analytics?.read ?? null,
        qualityScore:
          t.quality_score && t.quality_score !== "UNKNOWN"
            ? t.quality_score
            : null,
        lastUpdated: t.last_updated_time || null,
      }));
      setTemplates(mapped);
    } catch (err) {
      console.error("Failed to fetch templates:", err);
      setError("Failed to load templates. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const filteredTemplates = useMemo(() => {
    const now = new Date();
    return templates.filter((template) => {
      const matchesSearch =
        !search ||
        template.name.toLowerCase().includes(search.toLowerCase());
      const matchesLanguage =
        !languageFilter || template.language === languageFilter;
      const matchesStatus = !statusFilter || template.status === statusFilter;

      let matchesDate = true;
      if (dateRange && template.lastUpdated) {
        const updated = new Date(template.lastUpdated);
        if (dateRange === "today") {
          matchesDate =
            updated.toDateString() === now.toDateString();
        } else if (dateRange === "week") {
          const weekAgo = new Date(now);
          weekAgo.setDate(weekAgo.getDate() - 7);
          matchesDate = updated >= weekAgo;
        } else if (dateRange === "month") {
          const monthAgo = new Date(now);
          monthAgo.setDate(monthAgo.getDate() - 30);
          matchesDate = updated >= monthAgo;
        }
      }

      return matchesSearch && matchesLanguage && matchesStatus && matchesDate;
    });
  }, [templates, search, languageFilter, statusFilter, dateRange]);

  const activeTemplatesCount = templates.filter(
    (t) => t.status === "APPROVED",
  ).length;

  const languages = useMemo(
    () => [...new Set(templates.map((t) => t.language))].sort(),
    [templates],
  );

  const statuses = useMemo(
    () => [...new Set(templates.map((t) => t.status))].sort(),
    [templates],
  );

  const handleCreateTemplate = () => {
    navigate("/chat/templates/create");
  };

  // Common header cell style
  const headerCellSx = {
    fontWeight: 500,
    fontSize: "11px",
    color: "#6B7280",
    textTransform: "uppercase",
    letterSpacing: "0.02em",
    borderBottom: "1px solid #E5E7EB",
    py: 1.5,
    whiteSpace: "nowrap",
  };

  // Common body cell style
  const bodyCellSx = {
    borderBottom: "1px solid #F3F4F6",
    py: 2,
  };

  return (
    <Box sx={{ bgcolor: "#FAFAFA", minHeight: "100vh" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
          bgcolor: "#fff",
          borderBottom: "1px solid #E5E7EB",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography
            onClick={() => navigate("/chat/inbox")}
            sx={{
              fontSize: "14px",
              color: "#6B7280",
              fontWeight: 400,
              cursor: "pointer",
              "&:hover": {
                color: theme.palette.primary.main,
              },
            }}
          >
            Chat
          </Typography>
          <Typography sx={{ color: "#6B7280", fontSize: "14px" }}>/</Typography>
          <Typography
            sx={{
              fontSize: "14px",
              color: "#111827",
              fontWeight: 600,
            }}
          >
            Templates
          </Typography>
        </Stack>

      </Box>

      {/* Main Content */}
      <Box sx={{ p: 3 }}>
        {/* Filter Bar Card */}
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
            flexWrap: "wrap",
            gap: 1.5,
            p: 2,
            borderRadius: "18px",
            border: "1px solid #E5E7EB",
            bgcolor: "#fff",
          }}
        >
          {/* Left side - Search and Filters */}
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            flexWrap="wrap"
          >
            {/* Search */}
            <TextField
              placeholder="Search"
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                width: 180,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "20px",
                  bgcolor: "#fff",
                  height: 36,
                  "& fieldset": {
                    borderColor: "#E5E7EB",
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={16} color="#9CA3AF" />
                  </InputAdornment>
                ),
              }}
            />

            {/* Language Filter */}
            <FormControl size="small">
              <Select
                value={languageFilter}
                onChange={(e) => setLanguageFilter(e.target.value)}
                displayEmpty
                sx={{
                  borderRadius: "20px",
                  bgcolor: "#fff",
                  height: 36,
                  minWidth: 100,
                  "& fieldset": {
                    borderColor: "#E5E7EB",
                  },
                  "& .MuiSelect-select": {
                    py: 0.75,
                    fontSize: "13px",
                  },
                }}
                IconComponent={(props) => (
                  <ChevronDown
                    size={16}
                    {...props}
                    style={{ marginRight: 8 }}
                  />
                )}
              >
                <MenuItem value="">Language</MenuItem>
                {languages.map((l) => (
                  <MenuItem key={l} value={l}>
                    {l}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Status Filter */}
            <FormControl size="small">
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                displayEmpty
                sx={{
                  borderRadius: "20px",
                  bgcolor: "#fff",
                  height: 36,
                  minWidth: 90,
                  "& fieldset": {
                    borderColor: "#E5E7EB",
                  },
                  "& .MuiSelect-select": {
                    py: 0.75,
                    fontSize: "13px",
                  },
                }}
                IconComponent={(props) => (
                  <ChevronDown
                    size={16}
                    {...props}
                    style={{ marginRight: 8 }}
                  />
                )}
              >
                <MenuItem value="">Status</MenuItem>
                {statuses.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Date Range Filter */}
            <FormControl size="small">
              <Select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                displayEmpty
                sx={{
                  borderRadius: "20px",
                  bgcolor: "#fff",
                  height: 36,
                  minWidth: 110,
                  "& fieldset": {
                    borderColor: "#E5E7EB",
                  },
                  "& .MuiSelect-select": {
                    py: 0.75,
                    fontSize: "13px",
                  },
                }}
                IconComponent={(props) => (
                  <ChevronDown
                    size={16}
                    {...props}
                    style={{ marginRight: 8 }}
                  />
                )}
              >
                <MenuItem value="">Date Range</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="week">This Week</MenuItem>
                <MenuItem value="month">This Month</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          {/* Right side - Action Buttons */}
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button
              variant="outlined"
              startIcon={<RefreshCw size={16} />}
              onClick={fetchTemplates}
              disabled={loading}
              sx={{
                borderRadius: "20px",
                textTransform: "none",
                borderColor: "#E5E7EB",
                color: "#374151",
                fontWeight: 500,
                fontSize: "13px",
                height: 36,
                px: 2,
                "&:hover": {
                  borderColor: "#D1D5DB",
                  bgcolor: "#F9FAFB",
                },
              }}
            >
              Refresh
            </Button>

            <Button
              variant="contained"
              startIcon={<Plus size={16} />}
              onClick={handleCreateTemplate}
              sx={{
                borderRadius: "20px",
                textTransform: "none",
                bgcolor: theme.palette.primary.main,
                fontWeight: 500,
                fontSize: "13px",
                height: 36,
                px: 2,
                boxShadow: "none",
                "&:hover": {
                  bgcolor: theme.palette.primary.dark,
                  boxShadow: "none",
                },
              }}
            >
              Create Template
            </Button>
          </Stack>
        </Paper>

        {analyticsDisabled && !loading && (
          <Alert
            severity="warning"
            sx={{
              mb: 2,
              borderRadius: "12px",
              "& .MuiAlert-message": { fontSize: "13px" },
            }}
          >
            <strong>Template Insights not enabled</strong> — Meta Template
            Analytics (Sent, Delivered, Read) are currently unavailable for this
            WABA. To enable them, please visit your WhatsApp Business Manager
            settings.
          </Alert>
        )}

        {/* Table */}
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: "19px",
            boxShadow: "none",
            border: "1px solid #E5E7EB",
            overflow: "hidden",
          }}
        >
          <Table sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: "#F9FAFB" }}>
                <TableCell sx={{ ...headerCellSx, width: 50, pl: 2 }} />
                <TableCell sx={{ ...headerCellSx, minWidth: 160 }}>
                  <Stack direction="row" alignItems="center">
                    <span>Template Name</span>
                    <ChevronsUpDown
                      size={12}
                      color="#9CA3AF"
                      style={{ marginLeft: 4 }}
                    />
                  </Stack>
                </TableCell>
                <TableCell sx={{ ...headerCellSx, minWidth: 100 }}>
                  <Stack direction="row" alignItems="center">
                    <span>Category</span>
                    <ChevronsUpDown
                      size={12}
                      color="#9CA3AF"
                      style={{ marginLeft: 4 }}
                    />
                  </Stack>
                </TableCell>
                <TableCell sx={{ ...headerCellSx, minWidth: 140 }}>
                  <Stack direction="row" alignItems="center">
                    <span>Language</span>
                    <ChevronsUpDown
                      size={12}
                      color="#9CA3AF"
                      style={{ marginLeft: 4 }}
                    />
                  </Stack>
                </TableCell>
                <TableCell sx={{ ...headerCellSx, minWidth: 120 }}>
                  <Stack direction="row" alignItems="center">
                    <span>Status</span>
                    <ChevronsUpDown
                      size={12}
                      color="#9CA3AF"
                      style={{ marginLeft: 4 }}
                    />
                  </Stack>
                </TableCell>
                <TableCell
                  sx={{ ...headerCellSx, minWidth: 70 }}
                  align="center"
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <span>Sent</span>
                    <Tooltip title="Number of messages sent">
                      <span>
                        <InfoIcon />
                      </span>
                    </Tooltip>
                  </Stack>
                </TableCell>
                <TableCell
                  sx={{ ...headerCellSx, minWidth: 80 }}
                  align="center"
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <span>Delivered</span>
                    <Tooltip title="Number of messages delivered">
                      <span>
                        <InfoIcon />
                      </span>
                    </Tooltip>
                  </Stack>
                </TableCell>
                <TableCell
                  sx={{ ...headerCellSx, minWidth: 80 }}
                  align="center"
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <span>Read</span>
                    <Tooltip title="Number of messages read">
                      <span>
                        <InfoIcon />
                      </span>
                    </Tooltip>
                  </Stack>
                </TableCell>
                <TableCell
                  sx={{ ...headerCellSx, minWidth: 90 }}
                  align="center"
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <span>Quality</span>
                    <Tooltip title="Template quality score from Meta">
                      <span>
                        <InfoIcon />
                      </span>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={28} />
                    <Typography
                      sx={{ mt: 1, fontSize: "13px", color: "#6B7280" }}
                    >
                      Loading templates...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                    <Typography
                      sx={{ fontSize: "13px", color: "#DC2626", mb: 1 }}
                    >
                      {error}
                    </Typography>
                    <Button
                      size="small"
                      onClick={fetchTemplates}
                      variant="outlined"
                      sx={{ textTransform: "none" }}
                    >
                      Retry
                    </Button>
                  </TableCell>
                </TableRow>
              ) : filteredTemplates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                    <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
                      No templates found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredTemplates.map((template) => (
                  <TableRow
                    key={template.id}
                    hover
                    sx={{
                      cursor: "pointer",
                      bgcolor: "#fff",
                      "&:hover": { bgcolor: "#FAFAFA" },
                    }}
                    onClick={() => setSelectedId(template.id)}
                  >
                    <TableCell sx={{ ...bodyCellSx, pl: 2 }}>
                      <Radio
                        checked={selectedId === template.id}
                        onChange={() => setSelectedId(template.id)}
                        size="small"
                        sx={{
                          p: 0,
                          color: "#D1D5DB",
                          "&.Mui-checked": {
                            color: theme.palette.primary.main,
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Typography
                        sx={{
                          fontWeight: 400,
                          fontSize: "13px",
                          color: "#111827",
                        }}
                      >
                        {template.name}
                      </Typography>
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Typography sx={{ fontSize: "13px", color: "#374151" }}>
                        {template.category}
                      </Typography>
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "13px",
                            color: "#111827",
                            fontWeight: 500,
                          }}
                        >
                          {template.language}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "11px",
                            color: "#9CA3AF",
                            mt: 0.25,
                          }}
                        >
                          {template.languagePreview}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      {getStatusChip(template.status)}
                    </TableCell>
                    <TableCell sx={bodyCellSx} align="center">
                      <Typography
                        sx={{
                          fontSize: "13px",
                          color: template.sent != null ? "#374151" : "#9CA3AF",
                        }}
                      >
                        {template.sent ?? "--"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={bodyCellSx} align="center">
                      <Typography
                        sx={{
                          fontSize: "13px",
                          color:
                            template.delivered != null ? "#374151" : "#9CA3AF",
                        }}
                      >
                        {template.delivered ?? "--"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={bodyCellSx} align="center">
                      <Typography
                        sx={{
                          fontSize: "13px",
                          color: template.read != null ? "#374151" : "#9CA3AF",
                        }}
                      >
                        {template.read ?? "--"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={bodyCellSx} align="center">
                      {template.qualityScore ? (
                        <Chip
                          label={template.qualityScore}
                          size="small"
                          sx={{
                            bgcolor:
                              template.qualityScore === "GREEN"
                                ? "#D1FAE5"
                                : template.qualityScore === "YELLOW"
                                  ? "#FEF9C3"
                                  : template.qualityScore === "RED"
                                    ? "#FEE2E2"
                                    : "#F3F4F6",
                            color:
                              template.qualityScore === "GREEN"
                                ? "#047857"
                                : template.qualityScore === "YELLOW"
                                  ? "#A16207"
                                  : template.qualityScore === "RED"
                                    ? "#DC2626"
                                    : "#6B7280",
                            fontWeight: 500,
                            fontSize: "10px",
                            height: "22px",
                            borderRadius: "4px",
                            border: "none",
                            "& .MuiChip-label": { px: 1 },
                          }}
                        />
                      ) : (
                        <Typography sx={{ fontSize: "13px", color: "#9CA3AF" }}>
                          --
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Footer */}
        <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>
            {filteredTemplates.length} message templates shown (total active
            templates: {activeTemplatesCount} of 6000)
          </Typography>
          <Tooltip title="Template quota information">
            <span>
              <InfoIcon />
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
}
